import { useState, useCallback } from 'react';
import { AreaChart, Area, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, Line, Brush, ReferenceArea } from 'recharts';
import { Button } from './ui/button';
import { TrendingUp, BarChart3, ChevronDown, Check, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Custom crosshair cursor component
const CustomCursor = ({ points, width, height, payload }) => {
  if (!points || points.length === 0) return null;
  const { x, y } = points[0];
  
  return (
    <g>
      {/* Vertical line */}
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={height}
        stroke="#888"
        strokeWidth={1}
        strokeDasharray="3 3"
        opacity={0.8}
      />
      {/* Horizontal line */}
      <line
        x1={70}
        y1={y}
        x2={width}
        y2={y}
        stroke="#888"
        strokeWidth={1}
        strokeDasharray="3 3"
        opacity={0.8}
      />
    </g>
  );
};

// Custom candlestick shape component
const CandlestickBar = (props) => {
  const { x, width, payload, yAxisScale, minPrice, maxPrice, chartHeight } = props;
  
  if (!payload || !yAxisScale) return null;
  
  const { open, close, high, low, isUp } = payload;
  const color = isUp ? '#22c55e' : '#ef4444';
  
  // Use the passed yAxisScale function to convert price to y coordinate
  const openY = yAxisScale(open);
  const closeY = yAxisScale(close);
  const highY = yAxisScale(high);
  const lowY = yAxisScale(low);
  
  // Safety check - if NaN, skip
  if (isNaN(openY) || isNaN(closeY) || isNaN(highY) || isNaN(lowY)) return null;
  
  const bodyTop = Math.min(openY, closeY);
  const bodyHeight = Math.max(Math.abs(closeY - openY), 2);
  const candleWidth = Math.max(width * 0.6, 4);
  const candleX = x + (width - candleWidth) / 2;
  const wickX = x + width / 2;
  
  return (
    <g>
      {/* Upper wick */}
      <line
        x1={wickX}
        y1={highY}
        x2={wickX}
        y2={bodyTop}
        stroke={color}
        strokeWidth={1}
      />
      {/* Lower wick */}
      <line
        x1={wickX}
        y1={bodyTop + bodyHeight}
        x2={wickX}
        y2={lowY}
        stroke={color}
        strokeWidth={1}
      />
      {/* Body */}
      <rect
        x={candleX}
        y={bodyTop}
        width={candleWidth}
        height={bodyHeight}
        fill={color}
        stroke={color}
        strokeWidth={1}
      />
    </g>
  );
};

// Indicator definitions with colors
const INDICATORS = {
  sma: { label: 'SMA (7)', color: '#f59e0b', description: 'Simple Moving Average' },
  ema: { label: 'EMA (12)', color: '#8b5cf6', description: 'Exponential Moving Average' },
  rsi: { label: 'RSI (14)', color: '#3b82f6', description: 'Relative Strength Index' },
  macd: { label: 'MACD', color: '#06b6d4', description: 'Moving Avg Convergence Divergence' },
  bb: { label: 'Bollinger Bands', color: '#ec4899', description: '20-period with 2 std dev' },
  vwap: { label: 'VWAP', color: '#10b981', description: 'Volume Weighted Avg Price' },
};

export function PriceChart({ 
  data = [], 
  height = 400,
  showVolume = true,
  loading = false
}) {
  const [chartType, setChartType] = useState('line'); // 'line' or 'candle'
  const [indicatorDropdownOpen, setIndicatorDropdownOpen] = useState(false);
  
  // Zoom state - store the visible data range indices
  const [zoomLeft, setZoomLeft] = useState(null);
  const [zoomRight, setZoomRight] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  
  // Drag-to-zoom state
  const [refAreaLeft, setRefAreaLeft] = useState('');
  const [refAreaRight, setRefAreaRight] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  
  // Individual indicator toggles - default to OFF
  const [activeIndicators, setActiveIndicators] = useState({
    sma: false,
    ema: false,
    rsi: false,
    macd: false,
    bb: false,
    vwap: false,
  });

  const toggleIndicator = (key) => {
    setActiveIndicators(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const activeCount = Object.values(activeIndicators).filter(Boolean).length;

  // Reset zoom to show all data
  const resetZoom = useCallback(() => {
    setZoomLeft(null);
    setZoomRight(null);
    setIsZoomed(false);
    setRefAreaLeft('');
    setRefAreaRight('');
  }, []);

  // Zoom in by 25% from each side
  const zoomIn = useCallback(() => {
    if (!data || data.length === 0) return;
    const currentLeft = zoomLeft ?? 0;
    const currentRight = zoomRight ?? data.length - 1;
    const range = currentRight - currentLeft;
    const zoomAmount = Math.floor(range * 0.25);
    
    if (range > 4) {
      setZoomLeft(currentLeft + zoomAmount);
      setZoomRight(currentRight - zoomAmount);
      setIsZoomed(true);
    }
  }, [data, zoomLeft, zoomRight]);

  // Zoom out by 25%
  const zoomOut = useCallback(() => {
    if (!data || data.length === 0) return;
    const currentLeft = zoomLeft ?? 0;
    const currentRight = zoomRight ?? data.length - 1;
    const range = currentRight - currentLeft;
    const zoomAmount = Math.floor(range * 0.25);
    
    const newLeft = Math.max(0, currentLeft - zoomAmount);
    const newRight = Math.min(data.length - 1, currentRight + zoomAmount);
    
    if (newLeft === 0 && newRight === data.length - 1) {
      resetZoom();
    } else {
      setZoomLeft(newLeft);
      setZoomRight(newRight);
      setIsZoomed(true);
    }
  }, [data, zoomLeft, zoomRight, resetZoom]);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4" style={{ height }}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4" style={{ height }}>
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p>No chart data available for this period</p>
        </div>
      </div>
    );
  }

  // Calculate Simple Moving Average (SMA)
  const calculateSMA = (data, period, key = 'close') => {
    return data.map((item, index) => {
      if (index < period - 1) return null;
      const slice = data.slice(index - period + 1, index + 1);
      const sum = slice.reduce((acc, d) => acc + (Number(d[key]) || Number(d.price) || 0), 0);
      return sum / period;
    });
  };

  // Calculate Exponential Moving Average (EMA)
  const calculateEMA = (data, period, key = 'close') => {
    const multiplier = 2 / (period + 1);
    const ema = [];
    
    // First EMA is SMA
    let sum = 0;
    for (let i = 0; i < period && i < data.length; i++) {
      sum += Number(data[i][key]) || Number(data[i].price) || 0;
    }
    ema[period - 1] = sum / period;
    
    // Calculate rest of EMA
    for (let i = period; i < data.length; i++) {
      const price = Number(data[i][key]) || Number(data[i].price) || 0;
      ema[i] = (price - ema[i - 1]) * multiplier + ema[i - 1];
    }
    
    return ema;
  };

  // Calculate RSI (Relative Strength Index)
  const calculateRSI = (data, period = 14) => {
    if (data.length < period + 1) return data.map(() => null);
    
    const rsi = new Array(data.length).fill(null);
    const gains = [];
    const losses = [];
    
    // Calculate price changes
    for (let i = 1; i < data.length; i++) {
      const change = (Number(data[i].close) || Number(data[i].price)) - 
                     (Number(data[i-1].close) || Number(data[i-1].price));
      gains.push(change > 0 ? change : 0);
      losses.push(change < 0 ? Math.abs(change) : 0);
    }
    
    // First RSI calculation
    let avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
    let avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;
    
    if (avgLoss === 0) {
      rsi[period] = 100;
    } else {
      const rs = avgGain / avgLoss;
      rsi[period] = 100 - (100 / (1 + rs));
    }
    
    // Subsequent RSI calculations using smoothed averages
    for (let i = period; i < gains.length; i++) {
      avgGain = (avgGain * (period - 1) + gains[i]) / period;
      avgLoss = (avgLoss * (period - 1) + losses[i]) / period;
      
      if (avgLoss === 0) {
        rsi[i + 1] = 100;
      } else {
        const rs = avgGain / avgLoss;
        rsi[i + 1] = 100 - (100 / (1 + rs));
      }
    }
    
    return rsi;
  };

  // Calculate MACD (Moving Average Convergence Divergence)
  const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
    if (data.length < slowPeriod + signalPeriod) {
      return { macd: [], signal: [], histogram: [] };
    }

    // Calculate EMAs
    const fastEMA = calculateEMA(data, fastPeriod);
    const slowEMA = calculateEMA(data, slowPeriod);
    
    // MACD Line = Fast EMA - Slow EMA
    const macdLine = data.map((_, i) => {
      if (fastEMA[i] === undefined || slowEMA[i] === undefined) return null;
      return fastEMA[i] - slowEMA[i];
    });
    
    // Signal Line = EMA of MACD Line
    const macdData = macdLine.map((v, i) => ({ close: v, price: v }));
    const signalLine = calculateEMA(macdData, signalPeriod);
    
    // Histogram = MACD Line - Signal Line
    const histogram = macdLine.map((v, i) => {
      if (v === null || signalLine[i] === undefined) return null;
      return v - signalLine[i];
    });
    
    return { macd: macdLine, signal: signalLine, histogram };
  };

  // Calculate Bollinger Bands
  const calculateBollingerBands = (data, period = 20, stdDev = 2) => {
    const sma = calculateSMA(data, period);
    const upper = [];
    const lower = [];
    
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1 || sma[i] === null) {
        upper.push(null);
        lower.push(null);
        continue;
      }
      
      // Calculate standard deviation
      const slice = data.slice(i - period + 1, i + 1);
      const mean = sma[i];
      const squaredDiffs = slice.map(d => {
        const price = Number(d.close) || Number(d.price) || 0;
        return Math.pow(price - mean, 2);
      });
      const variance = squaredDiffs.reduce((a, b) => a + b, 0) / period;
      const sd = Math.sqrt(variance);
      
      upper.push(mean + (stdDev * sd));
      lower.push(mean - (stdDev * sd));
    }
    
    return { middle: sma, upper, lower };
  };

  // Calculate VWAP (Volume Weighted Average Price)
  const calculateVWAP = (data) => {
    let cumulativeTPV = 0; // Typical Price * Volume
    let cumulativeVolume = 0;
    
    return data.map((item, i) => {
      const typicalPrice = (Number(item.high) + Number(item.low) + Number(item.close)) / 3;
      const volume = Number(item.volume) || 1;
      
      cumulativeTPV += typicalPrice * volume;
      cumulativeVolume += volume;
      
      return cumulativeVolume > 0 ? cumulativeTPV / cumulativeVolume : null;
    });
  };

  // Process data for recharts
  const rawChartData = data.map(item => {
    // Support both 'time' and 'timestamp' field names
    let time = item.time || item.timestamp;
    if (typeof time === 'string') {
      time = new Date(time).toISOString();
    }
    
    const open = Number(item.open) || Number(item.close) || 0;
    const close = Number(item.close) || Number(item.price) || 0;
    const high = Number(item.high) || Math.max(open, close);
    const low = Number(item.low) || Math.min(open, close);
    
    return {
      time,
      price: close,
      open,
      high,
      low,
      close,
      volume: Number(item.volume) || 0,
      buy_volume: Number(item.buy_volume) || 0,
      sell_volume: Number(item.sell_volume) || 0,
      trade_count: Number(item.trade_count) || 0,
      buy_count: Number(item.buy_count) || 0,
      sell_count: Number(item.sell_count) || 0,
      isUp: close >= open,
    };
  }).filter(d => d.price > 0 && d.time);

  // Calculate indicators
  const smaPeriod = Math.min(7, Math.floor(rawChartData.length / 3));
  const emaPeriod = Math.min(12, Math.floor(rawChartData.length / 2));
  const rsiPeriod = Math.min(14, Math.floor(rawChartData.length / 2));
  const bbPeriod = Math.min(20, Math.floor(rawChartData.length / 2));
  
  const smaValues = smaPeriod > 1 ? calculateSMA(rawChartData, smaPeriod) : [];
  const emaValues = emaPeriod > 1 ? calculateEMA(rawChartData, emaPeriod) : [];
  const rsiValues = rsiPeriod > 1 ? calculateRSI(rawChartData, rsiPeriod) : [];
  const bbValues = bbPeriod > 1 ? calculateBollingerBands(rawChartData, bbPeriod) : { middle: [], upper: [], lower: [] };
  const vwapValues = calculateVWAP(rawChartData);
  
  // MACD with standard settings (12, 26, 9)
  const macdResult = rawChartData.length > 35 ? calculateMACD(rawChartData, 12, 26, 9) : { macd: [], signal: [], histogram: [] };
  
  // Merge indicators into chart data
  const chartData = rawChartData.map((item, i) => ({
    ...item,
    sma: smaValues[i] || null,
    ema: emaValues[i] || null,
    rsi: rsiValues[i] || null,
    macd: macdResult.macd[i] || null,
    macdSignal: macdResult.signal[i] || null,
    macdHistogram: macdResult.histogram[i] || null,
    bbUpper: bbValues.upper[i] || null,
    bbMiddle: bbValues.middle[i] || null,
    bbLower: bbValues.lower[i] || null,
    vwap: vwapValues[i] || null,
    index: i, // Add index for zoom reference
  }));

  // Apply zoom filter to get visible data
  const visibleData = (!isZoomed || zoomLeft === null || zoomRight === null)
    ? chartData
    : chartData.slice(zoomLeft, zoomRight + 1);

  // Zoom by drag-select handlers
  const handleMouseDown = (e) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isSelecting && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (refAreaLeft && refAreaRight) {
      const leftIndex = chartData.findIndex(d => d.time === refAreaLeft);
      const rightIndex = chartData.findIndex(d => d.time === refAreaRight);
      
      if (leftIndex !== -1 && rightIndex !== -1) {
        const [actualLeft, actualRight] = leftIndex < rightIndex 
          ? [leftIndex, rightIndex] 
          : [rightIndex, leftIndex];
        
        if (actualRight - actualLeft > 2) {
          setZoomLeft(actualLeft);
          setZoomRight(actualRight);
          setIsZoomed(true);
        }
      }
    }
    setRefAreaLeft('');
    setRefAreaRight('');
    setIsSelecting(false);
  };

  // Check if we have data for each indicator
  const hasRSIData = chartData.some(d => d.rsi !== null);
  const hasMACDData = chartData.some(d => d.macd !== null);
  const hasBBData = chartData.some(d => d.bbUpper !== null);
  const hasVWAPData = chartData.some(d => d.vwap !== null);

  // Determine which panels to show based on toggles AND data availability
  const showRSIPanel = activeIndicators.rsi && hasRSIData;
  const showMACDPanel = activeIndicators.macd && hasMACDData;
  
  let priceChartHeight, rsiChartHeight, macdChartHeight, volumeChartHeight;
  
  if (showVolume) {
    if (showRSIPanel && showMACDPanel) {
      priceChartHeight = Math.floor(height * 0.38);
      rsiChartHeight = Math.floor(height * 0.12);
      macdChartHeight = Math.floor(height * 0.15);
      volumeChartHeight = Math.floor(height * 0.15);
    } else if (showRSIPanel || showMACDPanel) {
      priceChartHeight = Math.floor(height * 0.45);
      rsiChartHeight = showRSIPanel ? Math.floor(height * 0.15) : 0;
      macdChartHeight = showMACDPanel ? Math.floor(height * 0.15) : 0;
      volumeChartHeight = Math.floor(height * 0.18);
    } else {
      priceChartHeight = Math.floor(height * 0.60);
      rsiChartHeight = 0;
      macdChartHeight = 0;
      volumeChartHeight = Math.floor(height * 0.22);
    }
  } else {
    priceChartHeight = height - 80;
    rsiChartHeight = 0;
    macdChartHeight = 0;
    volumeChartHeight = 0;
  }

  // Calculate min/max for candlestick using visible data
  const minPrice = Math.min(...visibleData.map(d => d.low));
  const maxPrice = Math.max(...visibleData.map(d => d.high));
  const pricePadding = (maxPrice - minPrice) * 0.1;
  
  // Create a scale function for candlestick Y coordinates
  // Maps price values to pixel coordinates (inverted because Y increases downward)
  const createYAxisScale = (chartHeight, padding = 5) => {
    const domainMin = minPrice - pricePadding;
    const domainMax = maxPrice + pricePadding;
    const rangeMin = padding;
    const rangeMax = chartHeight - padding;
    
    return (price) => {
      // Linear interpolation: map domain to range (inverted)
      const normalized = (price - domainMin) / (domainMax - domainMin);
      return rangeMax - (normalized * (rangeMax - rangeMin));
    };
  };
  
  // Create the scale with the chart height (approximate - will be refined when rendering)
  const yAxisScale = createYAxisScale(priceChartHeight - 10);

  return (
    <div className="rounded-lg border bg-card" style={{ overflow: 'visible' }} data-testid="tradingview-chart">
      {/* Chart Type Toggle, Zoom Controls & Indicators Dropdown */}
      <div className="flex items-center justify-between gap-2 p-2 border-b">
        <div className="flex items-center gap-2">
          <Button
            variant={chartType === 'line' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setChartType('line')}
            className="h-7 px-2"
          >
            <TrendingUp className="w-4 h-4 mr-1" />
            Line
          </Button>
          <Button
            variant={chartType === 'candle' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setChartType('candle')}
            className="h-7 px-2"
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            Candle
          </Button>
          
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l">
            <Button
              variant="ghost"
              size="sm"
              onClick={zoomIn}
              className="h-7 w-7 p-0"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={zoomOut}
              disabled={!isZoomed}
              className="h-7 w-7 p-0"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            {isZoomed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetZoom}
                className="h-7 px-2 text-xs"
                title="Reset Zoom"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </div>
        
        {/* Indicators Dropdown */}
        <Popover open={indicatorDropdownOpen} onOpenChange={setIndicatorDropdownOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 px-3">
              <span className="mr-1">Indicators</span>
              {activeCount > 0 && (
                <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center mr-1">
                  {activeCount}
                </span>
              )}
              <ChevronDown className="w-3 h-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="end">
            <div className="text-xs font-medium text-muted-foreground mb-2 px-2">Technical Indicators</div>
            <div className="space-y-1">
              {Object.entries(INDICATORS).map(([key, { label, color, description }]) => {
                // Check if data available for this indicator
                const isAvailable = key === 'rsi' ? hasRSIData :
                                   key === 'macd' ? hasMACDData :
                                   key === 'bb' ? hasBBData :
                                   key === 'vwap' ? hasVWAPData : true;
                
                return (
                  <button
                    key={key}
                    onClick={() => isAvailable && toggleIndicator(key)}
                    disabled={!isAvailable}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm transition-colors
                      ${activeIndicators[key] ? 'bg-muted' : 'hover:bg-muted/50'}
                      ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: color }}
                      />
                      <div className="text-left">
                        <div className="font-medium">{label}</div>
                        <div className="text-xs text-muted-foreground">{description}</div>
                      </div>
                    </div>
                    {activeIndicators[key] && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
            {activeCount > 0 && (
              <div className="mt-2 pt-2 border-t">
                <button
                  onClick={() => setActiveIndicators({ sma: false, ema: false, rsi: false, macd: false, bb: false, vwap: false })}
                  className="w-full text-xs text-muted-foreground hover:text-foreground text-center py-1"
                >
                  Clear All
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Active Indicators Legend */}
      {activeCount > 0 && (
        <div className="flex flex-wrap gap-2 px-4 py-1.5 border-b bg-muted/30">
          {Object.entries(activeIndicators).map(([key, isActive]) => {
            if (!isActive) return null;
            const { label, color } = INDICATORS[key];
            return (
              <span 
                key={key} 
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${color}20`, color }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                {label}
              </span>
            );
          })}
        </div>
      )}

      {/* Price Chart */}
      <div className="p-4 pb-0">
        <div style={{ height: priceChartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <AreaChart 
                data={visibleData} 
                margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <defs>
                  <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="bbGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 10, fill: '#888' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  }}
                  minTickGap={30}
                  allowDataOverflow
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#888' }}
                  tickFormatter={(value) => {
                    if (value < 0.0001) return `$${value.toFixed(6)}`;
                    if (value < 0.01) return `$${value.toFixed(5)}`;
                    if (value < 1) return `$${value.toFixed(4)}`;
                    return `$${value.toFixed(2)}`;
                  }}
                  domain={['auto', 'auto']}
                  width={70}
                />
                <Tooltip 
                  cursor={<CustomCursor />}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const data = payload[0].payload;
                    
                    const formatVol = (v) => {
                      if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
                      if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
                      return `$${v.toFixed(2)}`;
                    };
                    
                    return (
                      <div style={{ 
                        backgroundColor: 'rgba(0,0,0,0.95)', 
                        border: '1px solid #333',
                        borderRadius: '8px',
                        fontSize: '12px',
                        padding: '10px 12px',
                        minWidth: '200px'
                      }}>
                        <p style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>
                          {data.time ? new Date(data.time).toLocaleString() : 'N/A'}
                        </p>
                        <p style={{ color: '#10b981', fontWeight: 'bold', fontSize: '14px', marginBottom: '6px' }}>
                          Price: ${Number(data.price).toFixed(8)}
                        </p>
                        {data.volume > 0 && (
                          <>
                            <div style={{ borderTop: '1px solid #333', paddingTop: '6px', marginTop: '4px' }}>
                              <p style={{ color: '#fff', fontWeight: '500', fontSize: '12px', marginBottom: '4px' }}>
                                Volume: {formatVol(data.volume)}
                              </p>
                              <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                                <span style={{ color: '#22c55e' }}>Buy: {formatVol(data.buy_volume)}</span>
                                <span style={{ color: '#ef4444' }}>Sell: {formatVol(data.sell_volume)}</span>
                              </div>
                              <p style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
                                {data.trade_count} trades ({data.buy_count} buys, {data.sell_count} sells)
                              </p>
                            </div>
                          </>
                        )}
                        {(activeIndicators.sma && data.sma) && (
                          <p style={{ color: '#f59e0b', fontSize: '11px' }}>SMA(7): ${Number(data.sma).toFixed(8)}</p>
                        )}
                        {(activeIndicators.ema && data.ema) && (
                          <p style={{ color: '#8b5cf6', fontSize: '11px' }}>EMA(12): ${Number(data.ema).toFixed(8)}</p>
                        )}
                      </div>
                    );
                  }}
                />
                
                {/* Bollinger Bands Area */}
                {activeIndicators.bb && hasBBData && (
                  <Area
                    type="monotone"
                    dataKey="bbUpper"
                    stroke="#ec4899"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    fill="none"
                    dot={false}
                  />
                )}
                {activeIndicators.bb && hasBBData && (
                  <Area
                    type="monotone"
                    dataKey="bbLower"
                    stroke="#ec4899"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    fill="url(#bbGradient)"
                    dot={false}
                  />
                )}
                
                {/* Main Price Line */}
                <Area 
                  type="monotone" 
                  dataKey="price" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  fill="url(#priceGradient)"
                  dot={false}
                />
                
                {/* SMA Line */}
                {activeIndicators.sma && (
                  <Line 
                    type="monotone" 
                    dataKey="sma" 
                    stroke="#f59e0b" 
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                )}
                
                {/* EMA Line */}
                {activeIndicators.ema && (
                  <Line 
                    type="monotone" 
                    dataKey="ema" 
                    stroke="#8b5cf6" 
                    strokeWidth={1.5}
                    dot={false}
                  />
                )}
                
                {/* VWAP Line */}
                {activeIndicators.vwap && hasVWAPData && (
                  <Line 
                    type="monotone" 
                    dataKey="vwap" 
                    stroke="#10b981" 
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="2 2"
                  />
                )}
                
                {/* Zoom selection area */}
                {refAreaLeft && refAreaRight && (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    strokeOpacity={0.3}
                    fill="#10b981"
                    fillOpacity={0.2}
                  />
                )}
              </AreaChart>
            ) : (
              <ComposedChart 
                data={visibleData} 
                margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <defs>
                  <linearGradient id="bbGradientCandle" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 10, fill: '#888' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  }}
                  minTickGap={30}
                  allowDataOverflow
                />
                <YAxis 
                  yAxisId="price"
                  tick={{ fontSize: 10, fill: '#888' }}
                  tickFormatter={(value) => {
                    if (value < 0.0001) return `$${value.toFixed(6)}`;
                    if (value < 0.01) return `$${value.toFixed(5)}`;
                    if (value < 1) return `$${value.toFixed(4)}`;
                    return `$${value.toFixed(2)}`;
                  }}
                  domain={[minPrice - pricePadding, maxPrice + pricePadding]}
                  width={70}
                />
                <Tooltip 
                  cursor={<CustomCursor />}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const data = payload[0].payload;
                    
                    const formatVol = (v) => {
                      if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
                      if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
                      return `$${v.toFixed(2)}`;
                    };
                    
                    return (
                      <div style={{ 
                        backgroundColor: 'rgba(0,0,0,0.95)', 
                        border: '1px solid #333',
                        borderRadius: '8px',
                        fontSize: '12px',
                        padding: '10px 12px',
                        minWidth: '220px'
                      }}>
                        <p style={{ color: '#888', fontSize: '11px', marginBottom: '6px' }}>
                          {data.time ? new Date(data.time).toLocaleString() : 'N/A'}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: '6px' }}>
                          <span style={{ color: '#888' }}>O: <span style={{ color: '#fff' }}>${data.open.toFixed(8)}</span></span>
                          <span style={{ color: '#888' }}>H: <span style={{ color: '#22c55e' }}>${data.high.toFixed(8)}</span></span>
                          <span style={{ color: '#888' }}>L: <span style={{ color: '#ef4444' }}>${data.low.toFixed(8)}</span></span>
                          <span style={{ color: '#888' }}>C: <span style={{ color: data.isUp ? '#22c55e' : '#ef4444' }}>${data.close.toFixed(8)}</span></span>
                        </div>
                        {data.volume > 0 && (
                          <div style={{ borderTop: '1px solid #333', paddingTop: '6px' }}>
                            <p style={{ color: '#fff', fontWeight: '500', fontSize: '12px', marginBottom: '4px' }}>
                              Volume: {formatVol(data.volume)}
                            </p>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                              <span style={{ color: '#22c55e' }}>Buy: {formatVol(data.buy_volume)}</span>
                              <span style={{ color: '#ef4444' }}>Sell: {formatVol(data.sell_volume)}</span>
                            </div>
                            <p style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
                              {data.trade_count} trades ({data.buy_count} buys, {data.sell_count} sells)
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                
                {/* Bollinger Bands */}
                {activeIndicators.bb && hasBBData && (
                  <Line 
                    type="monotone" 
                    dataKey="bbUpper" 
                    stroke="#ec4899" 
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                    yAxisId="price"
                  />
                )}
                {activeIndicators.bb && hasBBData && (
                  <Line 
                    type="monotone" 
                    dataKey="bbLower" 
                    stroke="#ec4899" 
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                    yAxisId="price"
                  />
                )}
                
                {/* Candlestick bars */}
                <Bar 
                  dataKey="high" 
                  yAxisId="price"
                  shape={(props) => (
                    <CandlestickBar 
                      {...props} 
                      yAxisScale={yAxisScale}
                      minPrice={minPrice}
                      maxPrice={maxPrice}
                      chartHeight={priceChartHeight}
                    />
                  )}
                  name="candle"
                >
                  {visibleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.isUp ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
                
                {/* SMA Line */}
                {activeIndicators.sma && (
                  <Line 
                    type="monotone" 
                    dataKey="sma" 
                    stroke="#f59e0b" 
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="5 5"
                    yAxisId="price"
                  />
                )}
                
                {/* EMA Line */}
                {activeIndicators.ema && (
                  <Line 
                    type="monotone" 
                    dataKey="ema" 
                    stroke="#8b5cf6" 
                    strokeWidth={1.5}
                    dot={false}
                    yAxisId="price"
                  />
                )}
                
                {/* VWAP Line */}
                {activeIndicators.vwap && hasVWAPData && (
                  <Line 
                    type="monotone" 
                    dataKey="vwap" 
                    stroke="#10b981" 
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="2 2"
                    yAxisId="price"
                  />
                )}
                
                {/* Zoom selection area for candlestick */}
                {refAreaLeft && refAreaRight && (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    yAxisId="price"
                    strokeOpacity={0.3}
                    fill="#10b981"
                    fillOpacity={0.2}
                  />
                )}
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* RSI Panel */}
      {showRSIPanel && (
        <div className="px-4">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            RSI (14)
          </div>
          <div style={{ height: rsiChartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={visibleData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="rsiGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.2} />
                <XAxis dataKey="time" hide />
                <YAxis 
                  domain={[0, 100]} 
                  tick={{ fontSize: 9, fill: '#888' }}
                  width={70}
                  ticks={[30, 50, 70]}
                />
                <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.5} />
                <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="3 3" strokeOpacity={0.5} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: '1px solid #333', borderRadius: '8px', fontSize: '11px' }}
                  formatter={(value) => [value?.toFixed(2), 'RSI']}
                  labelFormatter={() => ''}
                />
                <Area 
                  type="monotone" 
                  dataKey="rsi" 
                  stroke="#3b82f6" 
                  strokeWidth={1.5}
                  fill="url(#rsiGradient)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* MACD Panel */}
      {showMACDPanel && (
        <div className="px-4">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-cyan-500" />
            MACD (12, 26, 9)
          </div>
          <div style={{ height: macdChartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={visibleData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.2} />
                <XAxis dataKey="time" hide />
                <YAxis 
                  tick={{ fontSize: 9, fill: '#888' }}
                  width={70}
                  tickFormatter={(value) => value.toExponential(1)}
                />
                <ReferenceLine y={0} stroke="#666" strokeWidth={1} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: '1px solid #333', borderRadius: '8px', fontSize: '11px' }}
                  formatter={(value, name) => {
                    if (name === 'macdHistogram') return [value?.toExponential(2), 'Histogram'];
                    if (name === 'macd') return [value?.toExponential(2), 'MACD'];
                    if (name === 'macdSignal') return [value?.toExponential(2), 'Signal'];
                    return [value, name];
                  }}
                  labelFormatter={() => ''}
                />
                {/* Histogram bars */}
                <Bar dataKey="macdHistogram" name="macdHistogram">
                  {visibleData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.macdHistogram >= 0 ? '#22c55e' : '#ef4444'} 
                      fillOpacity={0.7}
                    />
                  ))}
                </Bar>
                {/* MACD Line */}
                <Line 
                  type="monotone" 
                  dataKey="macd" 
                  stroke="#06b6d4" 
                  strokeWidth={1.5}
                  dot={false}
                />
                {/* Signal Line */}
                <Line 
                  type="monotone" 
                  dataKey="macdSignal" 
                  stroke="#f59e0b" 
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="3 3"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Volume Chart */}
      {showVolume && (
        <div className="px-4 pb-4" style={{ overflow: 'visible', position: 'relative' }}>
          <div className="text-xs text-muted-foreground mb-1">Volume</div>
          <div style={{ height: volumeChartHeight, overflow: 'visible' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={visibleData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.2} />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 9, fill: '#888' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  }}
                  minTickGap={50}
                />
                <YAxis 
                  tick={{ fontSize: 9, fill: '#888' }}
                  tickFormatter={(value) => {
                    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
                    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
                    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
                    return value.toFixed(0);
                  }}
                  width={70}
                />
                <Tooltip 
                  cursor={{ fill: 'rgba(255,255,255,0.1)' }}
                  allowEscapeViewBox={{ x: true, y: true }}
                  wrapperStyle={{ zIndex: 1000 }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    const data = payload[0].payload;
                    const volume = Number(data.volume) || 0;
                    const buyVol = Number(data.buy_volume) || 0;
                    const sellVol = Number(data.sell_volume) || 0;
                    const tradeCount = data.trade_count || 0;
                    const buyCount = data.buy_count || 0;
                    const sellCount = data.sell_count || 0;
                    
                    const formatVol = (v) => {
                      if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
                      if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
                      return `$${v.toFixed(2)}`;
                    };
                    
                    return (
                      <div style={{ 
                        backgroundColor: 'rgba(0,0,0,0.95)', 
                        border: '1px solid #444', 
                        borderRadius: '8px', 
                        padding: '10px', 
                        minWidth: '180px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                      }}>
                        <p style={{ color: '#888', fontSize: '11px', marginBottom: '6px' }}>
                          {data.time ? new Date(data.time).toLocaleString() : 'N/A'}
                        </p>
                        <p style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px', marginBottom: '8px' }}>
                          Volume: {formatVol(volume)}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                          <div>
                            <p style={{ color: '#22c55e', fontSize: '12px', fontWeight: '500' }}>Buy: {formatVol(buyVol)}</p>
                            <p style={{ color: '#666', fontSize: '10px' }}>{buyCount} trades</p>
                          </div>
                          <div>
                            <p style={{ color: '#ef4444', fontSize: '12px', fontWeight: '500' }}>Sell: {formatVol(sellVol)}</p>
                            <p style={{ color: '#666', fontSize: '10px' }}>{sellCount} trades</p>
                          </div>
                        </div>
                        <p style={{ color: '#666', fontSize: '10px', marginTop: '6px', borderTop: '1px solid #333', paddingTop: '6px' }}>
                          Total: {tradeCount} trades
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="volume" radius={[2, 2, 0, 0]}>
                  {visibleData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.isUp ? '#22c55e' : '#ef4444'} 
                      fillOpacity={0.6}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
